import styled, { css } from 'styled-components'
import {
  SpecialButton,
  SpecialOutlineButtonLink,
} from '@chegg-wt/core/theme/fanta/button'
import { spacing, colors, breakpoints } from '@chegg-ui/theme'
import { getNumbersInPxString } from '@chegg-wt/core/utils/regex'

import FantaTheme from '@chegg-wt/core/theme/fanta/theme'
import { AspiraStack } from '@chegg-wt/core/theme/fanta/fonts'
import { AspiraRegular, AspiraBold, FontSize12 } from '@chegg-wt/core/theme/sunkist/fonts'
import { ButtonReset } from '@chegg-wt/core/theme/sunkist/resets'
import { values } from '@chegg-wt/core/theme/sunkist/spacing'
import { SideViewProps } from '@chegg-wt/core/types/styles'
import Icon from '../Icon'

const breakpointSmallMax = getNumbersInPxString(breakpoints.sm.max)

export const Container = styled.div`
  ${AspiraRegular}
`

export const SearchContainer = styled.div`
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor20};
`

export const Breadcrumbs = styled.div`
  ${AspiraRegular}
  ${FontSize12}
  > strong {
    ${AspiraBold}
  }
  padding: ${values.xxsmall} ${values.xsmall};
  display: flex;
  align-items: center;
`

export const SearchLink = styled.a`
  color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
`

export const Arrow = styled(Icon)`
  height: 17px;
  width: 17px;
  margin: 0;
`

export const JournalTabs = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.Colors.forInterface.interfaceColor15};
  margin-bottom: 12px;
  margin-right: 8px;
`

export const JournalTab = styled.button<{ active: boolean }>`
  ${ButtonReset}
  font-size: ${FantaTheme.font.text.size.md};
  font-weight: ${FantaTheme.font.weight.bold};
  margin-bottom: -1px;
  margin-right: 30px;
  line-height: 32px;

  &:hover {
    border-bottom: 7px solid ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    padding-bottom: 0px;
  }

  ${({ active }) =>
    active
      ? css`
          border-bottom: 7px solid
            ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
        `
      : 'padding-bottom: 7px;'}
`

export const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-content: space-between;
`

export const Result = styled.div.attrs({
  role: 'region',
  'aria-label': 'Result',
})<SideViewProps>`
  font-size: ${FantaTheme.font.text.size.sm};
  padding: ${FantaTheme.spacing.fanta.lg} ${FantaTheme.spacing.fanta.md};
  margin: ${FantaTheme.spacing.fanta.xs} 0 ${FantaTheme.spacing.fanta.xxs} 0;
  display: flex;
  justify-content: space-between;
  border: solid 1px ${FantaTheme.colors.greyLight};
  box-shadow: 0 0 4px 0 rgba(34, 34, 34, 0.24);
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};

  @media (max-width: ${({ hasSideView, theme }) =>
      breakpointSmallMax +
      (hasSideView ? getNumbersInPxString(theme.sideFrameSizes[0]) : 0)}px) {
    flex-direction: column;
    align-items: flex-start;
  }

  div.adds-wrapper + & {
    margin-top: ${FantaTheme.spacing.fanta.xxs};
  }
`
export const ResultTitle = styled.div`
  ${AspiraStack};
  font-size: 18px;
  font-weight: ${FantaTheme.font.weight.bold};
`
export const ResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 16px;
  overflow-wrap: break-word;

  & > * + * {
    margin-top: ${values.xsmall};
  }
`

export const ResultDescription = styled.div`
  ${AspiraStack};
  font-size: ${FantaTheme.font.text.size.sm};
  line-height: 18px;
`
export const ResultLink = styled.a`
  ${AspiraStack};
  background: none;
  border: none;
  padding: 0;
  color: ${colors.link};
  text-decoration: none;
  font-size: ${FantaTheme.font.text.size.sm};
  font-weight: ${FantaTheme.font.weight.bold};
  letter-spacing: 0;
  line-height: 17px;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`

export const ResultButton = styled(SpecialButton)<SideViewProps>`
  @media (max-width: ${({ hasSideView, theme }) =>
      breakpointSmallMax +
      (hasSideView ? getNumbersInPxString(theme.sideFrameSizes[0]) : 0)}px) {
    width: 100%;
    height: 44px;
    margin: 12px 0 0 0;
  }
`

export const ResultButtonText = styled.span`
  ${AspiraStack};
  font-size: ${FantaTheme.font.text.size.lg};
  font-weight: ${FantaTheme.font.weight.bold};
  line-height: 10px;
  letter-spacing: 0;
  margin-top: 2px;
`

export const MessageContainer = styled.div`
  margin-top: 40px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const LoadingDescription = styled.div`
  display: inline-block;
  width: 100%;
  max-width: 420px;
  margin-bottom: 20px;
  text-align: center;
`

export const Heading = styled.h2`
  margin-top: 20px;
  margin-bottom: 10px;
  font-size: 21px;
`

export const Body = styled.p`
  font-size: 14px;
  line-height: 145%;
  margin: 0 0 10px;
`

export const PrimaryButton = styled(SpecialOutlineButtonLink)`
  padding: 0 ${spacing.px12};
`

export const SearchText = styled.strong`
  word-break: break-word;
  max-width: 75%;
`
